import {
  CouponPlatformInstanceAppId,
  ICouponInstances,
  TCouponPlatformInstanceAppId,
} from '~/api/services/coupon-platform/index.type'
import krNumberToString, { toKRW } from '~/api/utils/krNumberToString'
import { DomesticOrderDiscountSelectModalCoupon } from '~/api/converters/domestic/common/Coupons/SelectCouponModal/index.type'
import { getIsNolCoupon } from '~/api/converters/domestic/common/Coupons/SelectCouponModal/propertyCoupons'
import { DomesticCouponApplicationType } from '~/api/services/coupon/index.type'
import { getIsCouponPlatformProductCoupon } from '~/api/converters/domestic/common/Coupons/MaxDiscountCoupons/getCouponPlatformMaxDiscount'
import { ItemType } from '~/api/routes/domestic/order/types/shared'

const UNLIMITED_COUPON_COUNT = 99 // api 에서 null 로 넘어오는경우 무제한으로 간주함.

const couponPlatformUnavailableReasons = {
  stock: 1,
  maxusecount: 2,
  'long_greater_than_or_equal_to/price': 3,
  'long_less_than_or_equal_to/price': 4,
}

const couponPlatformUnavailableReasonsText = {
  stock: '선착순 마감된 쿠폰입니다.',
  maxusecount: '1인당 사용 횟수를 초과했습니다.',
  'long_greater_than_or_equal_to/price': '{price}원 이상 상품에 적용 가능합니다.',
  'long_less_than_or_equal_to/price': '{price}원 이하 상품에 적용 가능합니다.',
}

/**
 * 국내 숙소 이외의 카테고리 상품쿠폰 사용 불가 사유 조회
 */
const getCouponPlatformUnavailableReasonText = (coupon: ICouponInstances): string | undefined => {
  if (!coupon?.violations?.length) {
    return undefined
  }

  const sortedViolations = coupon.violations
    .map(violation => violation.toLowerCase())
    .sort((a, b) => couponPlatformUnavailableReasons[a] - couponPlatformUnavailableReasons[b])

  const unavailableReason = sortedViolations[0]

  if (
    unavailableReason === 'long_greater_than_or_equal_to/price' ||
    unavailableReason === 'long_less_than_or_equal_to/price'
  ) {
    const price =
      coupon.redemptionPolicy.conditions?.find(
        condition => condition?.code?.toLowerCase() === unavailableReason
      )?.value ?? 0
    return couponPlatformUnavailableReasonsText[unavailableReason].replace(
      '{price}',
      krNumberToString(price)
    )
  }
  return couponPlatformUnavailableReasonsText[unavailableReason]
}

export type MaxCouponDiscountCouponPlatformRequiredParams =
  | {
      type: ItemType.Leisure
      id: number
      coupons: ICouponInstances[]
      typeIdx: number
      price: number | null
    }
  | {
      type: ItemType.Train | ItemType.Bus
      id?: undefined
      coupons: ICouponInstances[]
      typeIdx: number
      price: number | null
    }

export const getCouponSelectModalCouponPlatformList = ({
  coupons,
  typeIdx,
  item,
}: {
  coupons: ICouponInstances[]
  typeIdx: number
  item: Omit<MaxCouponDiscountCouponPlatformRequiredParams, 'coupons' | 'typeIdx' | 'price'>
}): DomesticOrderDiscountSelectModalCoupon[] => {
  return coupons.map(coupon => {
    const amount = coupon.confirmedDiscount
    const amountText = toKRW(amount)

    const unavailableReasonText = getCouponPlatformUnavailableReasonText(coupon)
    // 재고 확인 후 선착순 마감 여부
    const isSoldOut = !unavailableReasonText && coupon.redemptionPolicy.stock?.remain === 0
    const available = !(coupon.violations.length > 0) && !isSoldOut

    const convertedCouponCommon = {
      id: coupon.id,
      couponId: coupon.couponId,
      couponName: coupon.resource.title,
      amount,
      amountText,
      available,
      isNolCoupon: getIsNolCoupon(coupon.couponType),
      unavailableReasonText,
      typeIdx,
      allowAdditional: false, // 더하기 쿠폰은 국내숙소만 제공으로 coupon-platform app 은 false 로 하드코딩
      applicationType: DomesticCouponApplicationType.MAIN, // 더하기 쿠폰은 국내숙소만 제공으로 coupon-platform app 은 MAIN 으로 하드코딩
    }

    // 상품 쿠폰 여부
    const isB2BCoupon = getIsCouponPlatformProductCoupon(coupon.couponTarget)

    if (isB2BCoupon) {
      const remainCount = coupon.redemptionPolicy.stock?.remain ?? UNLIMITED_COUPON_COUNT
      const maxUseCount = coupon.redemptionPolicy.maxUseCount?.remain ?? UNLIMITED_COUPON_COUNT
      const appId: TCouponPlatformInstanceAppId =
        item.type === ItemType.Leisure
          ? CouponPlatformInstanceAppId.Dl
          : ItemType.Train === item.type
            ? CouponPlatformInstanceAppId.Tr
            : CouponPlatformInstanceAppId.Eb
      const productId = item.type === ItemType.Leisure ? (item.id as number) : 0

      return {
        ...convertedCouponCommon,
        isB2BCoupon: true,
        remainCount,
        maxUseCount,
        b2bCouponCacheStockKey: `${coupon.couponId}-${typeIdx}`,
        productInfo: {
          isProperty: false,
          appId,
          productId,
        },
      }
    }
    return { ...convertedCouponCommon, isB2BCoupon: false }
  })
}
